import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Users, ArrowRight, MapPin } from 'lucide-react';
import api from '@/lib/api';

export default function CaravansListPage() {
  const [caravans, setCaravans] = useState([]);
  const [parks, setParks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/caravans'), api.get('/parks')])
      .then(([cRes, pRes]) => {
        setCaravans(cRes.data);
        setParks(pRes.data);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, []);

  const getParkName = (parkId) => {
    const park = parks.find(p => p.id === parkId);
    return park ? park.name : '';
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B726A]">Loading...</div>;

  return (
    <div data-testid="caravans-list-page">
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1440px] mx-auto">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#CC5A3D] mb-3">Our Properties</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-medium text-[#1F2A25] mb-4">All Caravans</h1>
          <p className="text-base text-[#6B726A] mb-12 max-w-2xl">Browse our hand-picked selection of holiday caravans across three premium Hoburne parks in the New Forest area.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caravans.map(c => (
              <Link key={c.id} to={`/caravans/${c.slug}`} className="group" data-testid={`caravan-list-${c.slug}`}>
                <div className="bg-white border border-[#E6D5B8] overflow-hidden rounded-xl shadow-[0_8px_30px_rgba(30,63,51,0.04)] transition-transform duration-300 hover:-translate-y-1">
                  <div className="img-zoom-wrapper aspect-[4/3]">
                    <img src={c.images?.[0]?.url} alt={c.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-1 text-xs text-[#CC5A3D] font-medium mb-1">
                      <MapPin className="w-3 h-3" /> {getParkName(c.park_id)}
                    </div>
                    <h3 className="text-xl font-heading font-medium text-[#1F2A25] group-hover:text-[#CC5A3D] transition-colors mb-2">{c.name}</h3>
                    <p className="text-sm text-[#6B726A] line-clamp-2 mb-3">{c.description}</p>
                    <div className="flex items-center gap-4 text-xs text-[#6B726A] mb-4">
                      <span><Users className="w-3 h-3 inline mr-1" />Sleeps {c.sleeps}</span>
                      <span>{c.bedrooms} bed</span>
                      <span>{c.bathrooms} bath</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-[#E6D5B8] pt-3">
                      <span className="text-lg font-heading font-medium text-[#1E3F33]">From &pound;{c.pricing?.off_peak}<span className="text-xs text-[#6B726A] font-body">/night</span></span>
                      <span className="text-sm text-[#CC5A3D] font-medium flex items-center gap-1">View Details <ArrowRight className="w-3 h-3" /></span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
